// src/views/Quiz.jsx
import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import styles from "./Quiz.module.css";
import LoadingSpinner from "../components/LoadingSpinner";
import FeedbackModal from "../components/FeedbackModal";
import useGameLogic from "../hooks/useGameLogic";
import GamePopups from "../components/GamePopups";
import commonGameStyles from "./GameCommon.module.css";
import { usePreloadedData } from "../context/PreloadContext";

// --- Feedback Modal Constants (UNIQUE FOR QUIZ) ---
const FEEDBACK_FORM_URL =
    "https://docs.google.com/forms/d/e/1FAIpQLSfCYQQ3NJS3nRLoVB9Q1LtELQxQizAD37t1LMwJ_5-ei2dryA/viewform?usp=dialog";
const FEEDBACK_PROMPT_INTERVAL = 3;
const FEEDBACK_PROMPT_DISMISS_DAYS = 7;

// Local Storage Keys (unique for Quiz)
const LS_KEY_GAMES_PLAYED = "quiz_games_played_count";
const LS_KEY_LAST_PROMPT_TIME = "quiz_last_prompt_time";
const LS_KEY_DISMISSED_PERMANENTLY = "quiz_dismissed_permanently";
const LS_KEY_QUIZ_FIRST_COMPLETED = "quiz_first_completed";

// 🟢 Accept props so quiz works in Journey or standalone
function Quiz({ inJourney = false, onComplete, gameLength = 10 }) {
    const { user } = useAuth();
    const navigate = useNavigate();
    const autoAdvanceTimeoutRef = useRef(null);
    const backendApiUrl = process.env.REACT_APP_BACKEND_API_URL;

    const [selected, setSelected] = useState(null);
    const [showExplanation, setShowExplanation] = useState(false);
    const [score, setScore] = useState(0);
    const [done, setDone] = useState(false);
    const [timer, setTimer] = useState(30);
    const [showInstructions, setShowInstructions] = useState(true);
    const [isCorrect, setIsCorrect] = useState(false);

    const ACTIVITY_TYPE = "originalQuiz";
    // Defensive: the context may be null or preloadedData may be null while
    // the global preload runs. Avoid destructuring from a possibly-null
    // context and use optional chaining when checking `.quiz`.
    const _preloadContext = usePreloadedData();
    const preloadedData = _preloadContext ? _preloadContext.preloadedData : null;

    const {
        lives,
        showGameOverPopup,
        showStreakPopup,
        streakMessage,
        showEncouragementPopup,
        encouragementMessage,
        showFeedbackModal,
        handleCloseFeedbackModal,
        handleDismissFeedbackPermanently,
        handleCorrectAttempt,
        handleIncorrectAttempt,
        resetGameLogicStates,
        questions,
        current,
        setCurrent,
        loadingQuestions,
        error,
        loadQuizQuestions,
    } = useGameLogic({
        activityType: ACTIVITY_TYPE,
        initialLives: 5,
        gameSpecificEndCondition: done,
        currentScore: score,
        gameLength,
        inJourney,          // ✅ tell the hook we’re in Journey mode
        onComplete,         // ✅ let the hook call Journey’s onComplete
        feedbackConfig: {
            FEEDBACK_FORM_URL,
            FEEDBACK_PROMPT_INTERVAL,
            FEEDBACK_PROMPT_DISMISS_DAYS,
            LS_KEY_GAMES_PLAYED,
            LS_KEY_LAST_PROMPT_TIME,
            LS_KEY_DISMISSED_PERMANENTLY,
            LS_KEY_FIRST_COMPLETED: LS_KEY_QUIZ_FIRST_COMPLETED,
        },
        navigate,
    });

    // --- Load questions ---
    useEffect(() => {
        if (!user) return;
        resetGameLogicStates();
        // Only use preloaded quiz data if it's available and an array.
        if (preloadedData?.quiz) {
            loadQuizQuestions(preloadedData.quiz);
        } else {
            loadQuizQuestions();
        }
    }, [user, loadQuizQuestions, resetGameLogicStates, preloadedData]);

    // --- Start quiz ---
    const startQuiz = useCallback(() => {
        setShowInstructions(false);
        setTimer(30);
    }, []);

    // --- Next question ---
    const next = useCallback(() => {
        setSelected(null);
        setShowExplanation(false);
        setTimer(30);
        setIsCorrect(false);

        if (current + 1 < questions.length) {
            setCurrent((prev) => prev + 1);
        } else {
            setDone(true);
            // ❌ no direct onComplete call — useGameLogic handles it now
        }
    }, [current, questions.length, setCurrent]);

    // --- Timeout logic ---
    const handleTimeout = useCallback(() => {
        setSelected(null);
        setShowExplanation(true);
        setIsCorrect(false);
        handleIncorrectAttempt(questions[current]._id);
    }, [handleIncorrectAttempt, questions, current]);

    useEffect(() => {
        if (
            showExplanation ||
            done ||
            loadingQuestions ||
            questions.length === 0 ||
            showInstructions ||
            showGameOverPopup
        )
            return;
        const interval = setInterval(() => {
            setTimer((prev) => {
                if (prev === 1) {
                    clearInterval(interval);
                    handleTimeout();
                    return 30;
                }
                return prev - 1;
            });
        }, 1000);
        return () => clearInterval(interval);
    }, [
        current,
        showExplanation,
        done,
        loadingQuestions,
        questions.length,
        showInstructions,
        handleTimeout,
        showGameOverPopup,
    ]);

    // --- Auto advance after showing explanation ---
    useEffect(() => {
        if (!showExplanation || showGameOverPopup) return;
        autoAdvanceTimeoutRef.current = setTimeout(() => {
            next();
        }, 8000);
        return () => clearTimeout(autoAdvanceTimeoutRef.current);
    }, [showExplanation, next, showGameOverPopup]);

    // --- Select option ---
    const handleSelect = useCallback(
        (choiceIndex) => {
            if (showGameOverPopup) return;
            setSelected(choiceIndex);
        },
        [showGameOverPopup]
    );

    // --- Styling helper for options ---
    const getOptionClass = (choiceIndex) => {
        if (!questions || questions.length === 0 || current >= questions.length) {
            return styles.optionButton;
        }

        const currentQ = questions[current];
        const optionText = currentQ.options[choiceIndex];
        const isSelected = choiceIndex === selected;

        if (showExplanation) {
            const isCorrectOption = optionText === currentQ.correctAnswerText;
            if (isCorrectOption) return `${styles.optionButton} ${styles.correct}`;
            if (isSelected) return `${styles.optionButton} ${styles.incorrect}`;
            return styles.optionButton;
        }

        return isSelected
            ? `${styles.optionButton} ${styles.selected}`
            : styles.optionButton;
    };

    // --- Submit answer ---
    const handleSubmit = useCallback(async () => {
        if (selected === null || showGameOverPopup) return;
        setShowExplanation(true);

        const currentQ = questions[current];
        const selectedOptionText = currentQ.options[selected];
        const isCorrectAnswer = selectedOptionText === currentQ.correctAnswerText;
        setIsCorrect(isCorrectAnswer);

        if (isCorrectAnswer) {
            setScore((prev) => prev + 1);
            handleCorrectAttempt(currentQ._id);
        } else {
            handleIncorrectAttempt(currentQ._id);
        }

        // Save progress (skip guest)
        if (!user || !user.uid || user.uid === "guest-user") return;
        try {
            await axios.post(
                `${backendApiUrl}/user-progress`,
                {
                    questionId: currentQ._id,
                    activityType: ACTIVITY_TYPE,
                    answeredCorrectly: isCorrectAnswer,
                },
                { headers: { "X-User-Id": user.uid } }
            );
        } catch (err) {
            console.error("Quiz: Progress save failed", err.message);
        }
    }, [
        selected,
        current,
        questions,
        user,
        ACTIVITY_TYPE,
        backendApiUrl,
        showGameOverPopup,
        handleCorrectAttempt,
        handleIncorrectAttempt,
    ]);

    // --- Early returns ---
    const backgroundStyle = { backgroundImage: `url("/images/meccaimage.jpg")` };
    if (!user)
        return (
            <p className={styles.message}>Please login to access the quiz.</p>
        );
    if (loadingQuestions)
        return (
            <div className={styles.background} style={backgroundStyle}>
                <div className={styles.overlay}></div>
                <div className={styles.quizContainer}>
                    <LoadingSpinner />
                </div>
            </div>
        );
    if (error)
        return (
            <div className={styles.background} style={backgroundStyle}>
                <div className={styles.overlay}></div>
                <div className={styles.quizContainer}>
                    <p className={styles.errorMessage}>Error: {error}</p>
                    <button onClick={loadQuizQuestions} className={styles.proceedButton}>
                        Retry Loading Questions
                    </button>
                </div>
            </div>
        );
    if (questions.length === 0 && !loadingQuestions)
        return (
            <div className={styles.background} style={backgroundStyle}>
                <div className={styles.overlay}></div>
                <div className={styles.quizContainer}>
                    <p className={styles.message}>
                        No quiz questions found. Please seed the database or check backend
                        API.
                    </p>
                </div>
            </div>
        );

    const currentQ = questions[current];
    const progressPercent = ((current + 1) / questions.length) * 100;

    return (
        <div className={styles.background} style={backgroundStyle}>
            <div className={styles.overlay}></div>
            <div className={styles.quizContainer} style={{ position: "relative" }}>
                <h2 className={styles.title}>🧠 Quiz</h2>

                {/* --- Instructions --- */}
                {showInstructions ? (
                    <div className={styles.instructionsPopup}>
                        <h3>How to Play Islam Quiz:</h3>
                        <ul>
                            <li>Answer each multiple-choice question.</li>
                            <li>Submit before time runs out (30s each).</li>
                            <li>You lose a life for wrong answers.</li>
                            <li>
                                Game ends when lives are gone or all questions answered.
                            </li>
                        </ul>
                        <button className={styles.proceedButton} onClick={startQuiz}>
                            Proceed
                        </button>
                    </div>
                ) : done ? (
                    inJourney ? null : ( // ✅ no end-screen in Journey
                        <div className={styles.result}>
                            {showGameOverPopup ? (
                                <>
                                    <h3 className={commonGameStyles.gameOverTitle}>
                                        Game Over! 💔
                                    </h3>
                                    <p className={commonGameStyles.finalScore}>
                                        Your score: {score} / {questions.length}
                                    </p>
                                    <button
                                        onClick={() => {
                                            resetGameLogicStates();
                                            loadQuizQuestions();
                                        }}
                                        className={styles.proceedButton}
                                        style={{ marginTop: "15px" }}
                                    >
                                        Play Again
                                    </button>
                                </>
                            ) : (
                                <>
                                    <h3>Your score: {score} / {questions.length}</h3>
                                    <p>Well done! 🎉</p>
                                    <button
                                        onClick={() => navigate("/home")}
                                        className={styles.proceedButton}
                                    >
                                        Back to Home
                                    </button>
                                </>
                            )}
                        </div>
                    )
                ) : (
                    // --- Main Quiz UI ---
                    <>
                        <p className={styles.progressText}>
                            Question {current + 1} of {questions.length}
                        </p>
                        <div className={styles.progressWrapper}>
                            <div
                                className={styles.progressBar}
                                style={{ width: `${progressPercent}%` }}
                            ></div>
                        </div>
                        <div className={commonGameStyles.livesContainer}>
                            {[...Array(5)].map((_, i) => (
                                <span
                                    key={i}
                                    className={`${commonGameStyles.heartIcon} ${
                                        i < lives
                                            ? commonGameStyles.filledHeart
                                            : commonGameStyles.emptyHeart
                                    }`}
                                >
                  ❤️
                </span>
                            ))}
                        </div>

                        <div className={styles.timer}>⏱ {timer}s</div>
                        <h3 className={styles.question}>{currentQ.question}</h3>

                        <ul className={styles.options}>
                            {currentQ.options.map((option, index) => (
                                <li key={index}>
                                    <button
                                        className={getOptionClass(index)}
                                        onClick={() => handleSelect(index)}
                                        disabled={showExplanation}
                                    >
                                        {option}
                                    </button>
                                </li>
                            ))}
                        </ul>

                        {!showExplanation && (
                            <button
                                className={styles.submit}
                                onClick={handleSubmit}
                                disabled={selected === null}
                            >
                                Submit
                            </button>
                        )}

                        {/* Explanation */}
                        {showExplanation && (
                            <div
                                style={{
                                    position: "absolute",
                                    top: "50%",
                                    left: "50%",
                                    transform: "translate(-50%, -50%)",
                                    backgroundColor: "rgba(0, 0, 0, 0.75)",
                                    padding: "2rem",
                                    borderRadius: "1rem",
                                    width: "90%",
                                    maxWidth: "600px",
                                    textAlign: "center",
                                    color: "#eee",
                                    zIndex: 1000,
                                }}
                            >
                                <p
                                    style={{
                                        fontSize: "1.2rem",
                                        fontWeight: "bold",
                                        color: isCorrect ? "green" : "red",
                                    }}
                                >
                                    {isCorrect ? "✅ Correct, MashaAllah!" : "❌ Incorrect."}
                                </p>
                                <p>
                                    <strong>Explanation:</strong> {currentQ.explanation}
                                </p>
                                <p style={{ fontSize: "0.9rem", color: "#ccc" }}>
                                    <em>{currentQ.reference}</em>
                                </p>
                                <button
                                    className={styles.nextButton}
                                    onClick={next}
                                    disabled={showGameOverPopup}
                                >
                                    {current + 1 < questions.length
                                        ? "Next Question"
                                        : "View Results"}
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Popups and Feedback */}
            <GamePopups
                showStreakPopup={showStreakPopup}
                streakMessage={streakMessage}
                showEncouragementPopup={showEncouragementPopup}
                encouragementMessage={encouragementMessage}
            />
            {!inJourney && (
                <FeedbackModal
                    isOpen={showFeedbackModal}
                    onClose={handleCloseFeedbackModal}
                    onDismissPermanently={handleDismissFeedbackPermanently}
                    formUrl={FEEDBACK_FORM_URL}
                    gameName="Islam Quiz"
                />
            )}
        </div>
    );
}

export default Quiz;
